import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { apiGet, apiPost, getToken, setToken } from '@/shared/api/client';

export type Role = 'ADMIN' | 'SUPERVISOR' | 'NURSE_RT' | 'NURSE' | 'TECHNICIAN' | 'VIEWER';

export interface AuthUser {
  id: string;
  email: string;
  full_name: string;
  role: Role;
  org_id: string;
  org_slug: string;
  org_name?: string;
  sector_id?: string | null;
}

interface AuthContextValue {
  user: AuthUser | null;
  loading: boolean;
  loginPassword: (email: string, password: string, org_slug?: string) => Promise<void>;
  loginRfid: (rfid_hex: string, org_slug: string) => Promise<void>;
  logout: () => void;
  hasRole: (...roles: Role[]) => boolean;
  switchOrg: (target_org_id: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

interface LoginResponse {
  token: string;
  expires_in: number;
  user: AuthUser;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState<boolean>(!!getToken());

  const clear = useCallback(() => {
    setToken(null);
    setUser(null);
  }, []);

  useEffect(() => {
    let active = true;
    if (!getToken()) {
      setLoading(false);
      return;
    }
    apiGet<AuthUser>('/v1/me')
      .then((u) => { if (active) setUser(u); })
      .catch(() => { if (active) clear(); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [clear]);

  useEffect(() => {
    const handler = () => clear();
    window.addEventListener('ictus:auth-expired', handler);
    return () => window.removeEventListener('ictus:auth-expired', handler);
  }, [clear]);

  const loginPassword = useCallback(async (email: string, password: string, org_slug?: string) => {
    const resp = await apiPost<LoginResponse>('/v1/auth/login', { email, password, ...(org_slug ? { org_slug } : {}) }, { skipAuth: true });
    setToken(resp.token);
    const me = await apiGet<AuthUser>('/v1/me');
    setUser(me);
  }, []);

  const loginRfid = useCallback(async (rfid_hex: string, org_slug: string) => {
    const resp = await apiPost<LoginResponse>('/v1/auth/login-rfid', { rfid_hex, org_slug }, { skipAuth: true });
    setToken(resp.token);
    const me = await apiGet<AuthUser>('/v1/me');
    setUser(me);
  }, []);

  const logout = useCallback(() => { clear(); }, [clear]);

  const switchOrg = useCallback(async (target_org_id: string) => {
    const resp = await apiPost<LoginResponse>('/v1/auth/switch-org', { target_org_id });
    setToken(resp.token);
    const me = await apiGet<AuthUser>('/v1/me');
    setUser(me);
  }, []);

  const hasRole = useCallback((...roles: Role[]) => !!user && roles.includes(user.role), [user]);

  const value = useMemo<AuthContextValue>(() => ({
    user, loading, loginPassword, loginRfid, logout, hasRole, switchOrg,
  }), [user, loading, loginPassword, loginRfid, logout, hasRole, switchOrg]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within <AuthProvider>');
  return ctx;
}
